import {FC, useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button} from '../../../../common/Button';
import CurrentTaskInfo from '../common/Info';
import EnsurePermission from '../../../../common/EnsurePermission';
import Questionnaire from './questionnaire';
import permissionsInfo from '../../../../../constants/permissionsInfo';
import {CurrentTask} from '../../../types';
import {taskStateToColor} from '../common/helpers/taskStateToColor';

type DetailsProps = {
  task: CurrentTask,
  isCurrentUserAssignee: boolean,
  isRunning: boolean,
  hasClaimButton: boolean,
  isChecklistCompleted: boolean,
  alertRange: Nullable<number>,
  handleOpenClaimPopup: () => void,
  isSubmitting: boolean,
  submitTask: (values: Dictionary<string | string[]>) => void,
  notRunningStatus: boolean
}

const Details: FC<DetailsProps> = ({
  task,
  isRunning,
  isCurrentUserAssignee,
  hasClaimButton,
  handleOpenClaimPopup,
  isChecklistCompleted,
  alertRange,
  isSubmitting,
  submitTask,
  notRunningStatus
}) => {
  const {t} = useTranslation(['tasks', 'common']);
  const [sectionColor, setSectionColor] = useState(taskStateToColor.NORMAL);
  const {formFields, answers} = task || {};

  const onChangeProgressBarStateCallback = useCallback((state: string) => {
    const nextColor = taskStateToColor[state];

    if (nextColor !== sectionColor) {
      setSectionColor(nextColor);
    }
  }, [sectionColor]);

  return (
    <div className={`details ${sectionColor}`}>
      <CurrentTaskInfo
        task={task}
        isRunning={isRunning}
        isCurrentUserAssignee={isCurrentUserAssignee}
        onChangeProgressBarStateCallback={onChangeProgressBarStateCallback}
        alertRange={alertRange}
      />

      {!!formFields?.length && (
        <Questionnaire
          onSubmit={submitTask}
          formFields={formFields}
          answers={answers}
          isChecklistCompleted={isChecklistCompleted}
          isCurrentUserAssignee={isCurrentUserAssignee}
          isRunning={isRunning}
          isSubmitting={isSubmitting}
        />
      )}

      {hasClaimButton && (
        <EnsurePermission elementPermissions={permissionsInfo.tasks.claim}>
          <div className="current-task__button-container">
            <Button
              label={t('claim.btn')}
              onClick={handleOpenClaimPopup}
            />
          </div>
        </EnsurePermission>
      )}

      {isCurrentUserAssignee && isRunning && !formFields?.length && (
        <EnsurePermission
          elementPermissions={permissionsInfo.tasks.complete}
        >
          <div className="current-task__button-container">
            <Button
              label={t('common:complete')}
              type="button"
              onClick={() => submitTask({})}
              disabled={!isChecklistCompleted || isSubmitting}
            />
          </div>
        </EnsurePermission>
      )}
    </div>
  );
};

export default Details;
